import { useSelector } from "react-redux";
import { RootState } from "../app/types";
import { PreferencesState } from "../app/preferencesSlice";
import { getLocale } from "./getLocale";
import { LOCALE_PREFERENCE_KEY } from "./localePreferenceKey";

/**
 * Returns the currently active locale for the user.
 *
 * Resolution order:
 * 1. Saved user preference from `ecm-preferences` (key: "locale")
 * 2. Browser language (`navigator.language`)
 * 3. Default fallback (`en-US`)
 *
 * The returned locale is always constrained to the `SUPPORTED_LOCALES` list.
 *
 * @returns A supported BCP 47 locale code (e.g. "en-US", "fr-CA")
 */
export const useLocale = (): string => {
    const savedPreference = useSelector((state: RootState) => {
        const preferences = (state.preferences as PreferencesState | undefined)?.preferences;
        const raw = preferences?.find(p => p.preference === LOCALE_PREFERENCE_KEY)?.preferenceValue;
        if (!raw) return null;
        // preference_value is stored as JSON in the DB (e.g. `"fr-CA"` not `fr-CA`).
        // Parse it so getLocale receives a plain locale string.
        try {
            return JSON.parse(raw) as string;
        } catch {
            return raw;
        }
    });

    return getLocale(savedPreference);
};
